import { Moment } from 'moment-timezone';
import {
    getMarketplaceDeposit,
    getOnlineBookingPreferences
} from 'reducers/marketplace/MarketplaceSelectors';
import { OnlineBookingWalkingCache } from 'reducers/onlineBooking/OnlineBookingState';
import { createSelector } from 'reselect';
import {
    getOnlineBookingAddonLineQuantities,
    getOnlineBookingAddonUnits
} from 'utils/onlineBookingAddons';
import { RootState } from 'store';
import {
    MarketplaceDeposit,
    MarketplaceDepositApplicability,
    MarketplaceDepositCategory,
    MarketplaceDepositRule,
    MarketplaceServiceCategory
} from 'types/Marketplace';
import { Pet } from 'types/Pet';
import { DAYCARE_DURATION_OPTIONS } from 'types/Service';
import { Staff } from 'types/Staff';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingEvaluationItem,
    isOnlineBookingGroomingItem,
    isOnlineBookingSessionItemOf,
    OnlineBookingBoardingItem,
    OnlineBookingDaycareItem,
    OnlineBookingEvaluationItem,
    OnlineBookingGroomingItem,
    OnlineBookingItem,
    OnlineBookingSessionItem,
    OnlineBookingStep,
    OnlineBookingTime,
    SessionBusinessArea
} from 'types/OnlineBooking';
import { resolveWalkingCustomerAddressId } from 'types/Walking';
import {
    bookingRequiresApproval,
    calculateDaycareHours,
    getServicePriceByPet
} from 'utils/serviceUtils';
import { getWaitlistItemsTotalAmount } from 'utils/waitlistUtils';

export const getOnlineBookingState = (state: RootState) => ({
    ...state.onlineBooking,
    isReturningClient: !state.onlineBooking.customer.lead
});

export const getOnlineBookingCurrentStep = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.currentStep
);

export const getOnlineBookingCustomer = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.customer
);

export const getOnlineBookingItems = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.bookingItems
);

export const getOnlineBookingPets = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.selectedPets
);

export const getOnlineBookingGroomingItems = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(item =>
            isOnlineBookingGroomingItem(item)
        ) as Array<OnlineBookingGroomingItem>
);

export const getOnlineBookingGroomingItemsWithEvaluation = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(
            item =>
                isOnlineBookingGroomingItem(item) ||
                (isOnlineBookingEvaluationItem(item) &&
                    [
                        MarketplaceServiceCategory.GROOMING,
                        MarketplaceServiceCategory.DENTAL
                    ].includes(item.evaluationBusinessArea))
        ) as Array<OnlineBookingGroomingItem | OnlineBookingEvaluationItem>
);

export const getOnlineBookingDaycareItems = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(item =>
            isOnlineBookingDaycareItem(item)
        ) as Array<OnlineBookingDaycareItem>
);

export const getOnlineBookingDaycareItemsWithEvaluation = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(
            item =>
                isOnlineBookingDaycareItem(item) ||
                (isOnlineBookingEvaluationItem(item) &&
                    item.evaluationBusinessArea === MarketplaceServiceCategory.DAYCARE)
        ) as Array<OnlineBookingDaycareItem | OnlineBookingEvaluationItem>
);

export const getOnlineBookingBoardingItems = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(item =>
            isOnlineBookingBoardingItem(item)
        ) as Array<OnlineBookingBoardingItem>
);

export const getOnlineBookingBoardingItemsWithEvaluation = createSelector(
    getOnlineBookingItems,
    bookingItems =>
        bookingItems.filter(
            item =>
                isOnlineBookingBoardingItem(item) ||
                (isOnlineBookingEvaluationItem(item) &&
                    item.evaluationBusinessArea === MarketplaceServiceCategory.BOARDING)
        ) as Array<OnlineBookingBoardingItem | OnlineBookingEvaluationItem>
);

export const getOnlineBookingEvaluationItems = createSelector(getOnlineBookingItems, bookingItems =>
    bookingItems.filter(isOnlineBookingEvaluationItem)
);

export interface OnlineBookingSessionSelectors {
    items: (state: RootState) => Array<OnlineBookingSessionItem>;
    preferredStaff: (state: RootState) => Staff | undefined;
    date: (state: RootState) => Moment | undefined;
    time: (state: RootState) => OnlineBookingTime | undefined;
    eligibility: (state: RootState) => OnlineBookingWalkingCache['eligibility'];
    staffAvailability: (state: RootState) => OnlineBookingWalkingCache['staffAvailability'];
    availability: (state: RootState) => OnlineBookingWalkingCache['availability'];
    lastBookingError: (state: RootState) => string | undefined;
    lastBookingErrorItemUuid: (state: RootState) => string | undefined;
}

const createOnlineBookingSessionSelectors = (
    businessArea: SessionBusinessArea
): OnlineBookingSessionSelectors => {
    const items = createSelector(getOnlineBookingItems, bookingItems =>
        bookingItems.filter(isOnlineBookingSessionItemOf(businessArea))
    );
    const cache = (state: RootState) => state.onlineBooking[businessArea];

    return {
        items,
        preferredStaff: createSelector(items, sessionItems => sessionItems[0]?.preferredStaff),
        date: createSelector(items, sessionItems => sessionItems[0]?.date),
        time: createSelector(items, sessionItems => sessionItems[0]?.time),
        eligibility: createSelector(cache, sessionCache => sessionCache.eligibility),
        staffAvailability: createSelector(cache, sessionCache => sessionCache.staffAvailability),
        availability: createSelector(cache, sessionCache => sessionCache.availability),
        lastBookingError: createSelector(cache, sessionCache => sessionCache.lastBookingError),
        lastBookingErrorItemUuid: createSelector(
            cache,
            sessionCache => sessionCache.lastBookingErrorItemUuid
        )
    };
};

export const ONLINE_BOOKING_SESSION_SELECTORS: Record<
    SessionBusinessArea,
    OnlineBookingSessionSelectors
> = {
    [MarketplaceServiceCategory.WALKING]: createOnlineBookingSessionSelectors(
        MarketplaceServiceCategory.WALKING
    ),
    [MarketplaceServiceCategory.TRAINING]: createOnlineBookingSessionSelectors(
        MarketplaceServiceCategory.TRAINING
    )
};

const walkingSelectors = ONLINE_BOOKING_SESSION_SELECTORS[MarketplaceServiceCategory.WALKING];

export const getOnlineBookingWalkingItems = walkingSelectors.items;

export const getOnlineBookingTrainingItems =
    ONLINE_BOOKING_SESSION_SELECTORS[MarketplaceServiceCategory.TRAINING].items;

export const getOnlineBookingWalkingEligibility = walkingSelectors.eligibility;

export const getOnlineBookingWalkingStaffAvailability = walkingSelectors.staffAvailability;

export const getOnlineBookingWalkingAvailability = walkingSelectors.availability;

export const getOnlineBookingWalkingLastBookingError = walkingSelectors.lastBookingError;

export const getOnlineBookingWalkingLastBookingErrorItemUuid =
    walkingSelectors.lastBookingErrorItemUuid;

export const getOnlineBookingWalkingEligibilityByKey = (requestKey: string) =>
    createSelector(getOnlineBookingWalkingEligibility, eligibility => eligibility[requestKey]);

export const getOnlineBookingWalkingStaffAvailabilityByKey = (requestKey: string) =>
    createSelector(
        getOnlineBookingWalkingStaffAvailability,
        staffAvailability => staffAvailability[requestKey]
    );

export const getOnlineBookingWalkingAvailabilityByKey = (requestKey: string) =>
    createSelector(getOnlineBookingWalkingAvailability, availability => availability[requestKey]);

export const getOnlineBookingWalkingAddressId = createSelector(getOnlineBookingCustomer, customer =>
    resolveWalkingCustomerAddressId(customer.addresses)
);

export const getWalkingEligibleStaffIdsFromCache = (
    eligibilityCache: OnlineBookingWalkingCache['eligibility'],
    requestKey: string,
    serviceId: number
): Array<number> =>
    eligibilityCache[requestKey]?.data?.services.find(service => service.id === serviceId)
        ?.staffIds ?? [];

const groupOnlineBookingItemsByPet = (bookingItems: Array<OnlineBookingItem>) => {
    const groupedItems = new Map<
        number,
        {
            pet: Pet;
            items: Array<OnlineBookingItem>;
        }
    >();

    bookingItems.forEach(item => {
        const items = groupedItems.get(item.pet.id)?.items || [];

        groupedItems.set(item.pet.id, {
            pet: item.pet,
            items: [...items, item]
        });
    });

    return Array.from(groupedItems.values());
};

export const getOnlineBookingItemsGroupedByPet = createSelector(
    getOnlineBookingItems,
    groupOnlineBookingItemsByPet
);

/*
 * Waitlist
 */

// Cart items a waitlist request for `category` includes: grooming also takes dental
// items (they share the grooming item type). Evaluation items never go to a waitlist.
export const filterOnlineBookingWaitlistItems = (
    bookingItems: Array<OnlineBookingItem>,
    category?: MarketplaceServiceCategory
): Array<OnlineBookingItem> => {
    switch (category) {
        case MarketplaceServiceCategory.GROOMING:
        case MarketplaceServiceCategory.DENTAL:
            return bookingItems.filter(isOnlineBookingGroomingItem);
        case MarketplaceServiceCategory.DAYCARE:
            return bookingItems.filter(isOnlineBookingDaycareItem);
        case MarketplaceServiceCategory.BOARDING:
            return bookingItems.filter(isOnlineBookingBoardingItem);
        default:
            return [];
    }
};

export const getOnlineBookingWaitlistCategory = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.waitlistCategory
);

export const getOnlineBookingWaitlistItems = createSelector(
    [getOnlineBookingItems, getOnlineBookingWaitlistCategory],
    (bookingItems, waitlistCategory) =>
        filterOnlineBookingWaitlistItems(bookingItems, waitlistCategory)
);

export const getOnlineBookingWaitlistItemsGroupedByPet = createSelector(
    getOnlineBookingWaitlistItems,
    groupOnlineBookingItemsByPet
);

// Same line totals the waitlist request sends: the dates picked in the cart, or a
// single occurrence of each service when the customer asks for ASAP.
export const getOnlineBookingWaitlistTotalAmount = createSelector(
    [getOnlineBookingWaitlistItems, (_state: RootState, asap = false) => asap],
    (waitlistItems, asap) => getWaitlistItemsTotalAmount(waitlistItems, asap)
);

export const getOnlineBookingGroomingPreferredStaff = createSelector(
    getOnlineBookingState,
    ({ bookingItems }) => bookingItems.find(isOnlineBookingGroomingItem)?.preferredStaff
);

export const getOnlineBookingWalkingPreferredStaff = walkingSelectors.preferredStaff;

export const getOnlineBookingWalkingDate = walkingSelectors.date;

export const getOnlineBookingWalkingTime = walkingSelectors.time;

export const getOnlineBookingGroomingAvailableStaffs = createSelector(
    [
        (state: RootState) => state.staffs.staffs,
        (state: RootState) => state.onlineBooking.bookingItems
    ],
    (staffs, bookingItems) => {
        const groomingBookingItems = bookingItems.filter(item =>
            [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL].includes(
                item.businessArea
            )
        );

        return staffs.filter(staff =>
            groomingBookingItems.every(
                bookingItem => bookingItem.service?.staffIds.includes(staff.id)
            )
        );
    }
);

export const getOnlineBookingGroomingDate = createSelector(
    getOnlineBookingState,
    ({ bookingItems }) => bookingItems.find(isOnlineBookingGroomingItem)?.date
);

export const getOnlineBookingDaycareDates = createSelector(
    getOnlineBookingState,
    ({ bookingItems }) =>
        bookingItems.find(isOnlineBookingDaycareItem)?.days?.map(day => day.date) ?? []
);

export const getOnlineBookingDaycareAvailabilityByDate = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.daycareAvailabilityByDate
);

export const getOnlineBookingDaycareAvailabilityLoading = createSelector(
    getOnlineBookingState,
    onlineBooking =>
        Object.values(onlineBooking.daycareAvailabilityLoadingByDate).some(loading => loading)
);

export const getOnlineBookingBoardingAvailability = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.boardingAvailability
);

export const getOnlineBookingBoardingAvailabilityLoading = createSelector(
    getOnlineBookingState,
    onlineBooking => onlineBooking.boardingAvailabilityLoading
);

export const getOnlineBookingBoardingHasAvailability = createSelector(
    getOnlineBookingBoardingAvailability,
    availability => (availability?.dropOffSlots.length ?? 0) > 0
);

export const getOnlineBookingBoardingDate = createSelector(
    getOnlineBookingState,
    ({ bookingItems }) => {
        const boardingItem = bookingItems.filter(isOnlineBookingBoardingItem)[0];

        if (!boardingItem || !boardingItem.startDate || !boardingItem.endDate) {
            return null;
        }

        return {
            from: boardingItem.startDate,
            to: boardingItem.endDate
        };
    }
);

export const getOnlineBookingGroomingTime = createSelector(
    getOnlineBookingState,
    ({ bookingItems }) => bookingItems.find(isOnlineBookingGroomingItem)?.time
);

export const getOnlineBookingDaycareDayCount = (bookingItem: OnlineBookingItem): number =>
    isOnlineBookingDaycareItem(bookingItem) ? bookingItem.days?.length || 1 : 1;

/**
 * The deposit rule applying to a booking item, or `undefined` when the item
 * contributes nothing.
 *
 * In `all` applicability every item is priced by the marketplace-level rule. In
 * `specific` applicability the rule is looked up by the item's service
 * `businessArea` — the same key the server resolves against — and an item whose
 * category has no rule contributes zero.
 */
export const getDepositRuleForBookingItem = (
    bookingItem: OnlineBookingItem,
    deposit: MarketplaceDeposit
): MarketplaceDepositRule | undefined => {
    if (deposit.applicability === MarketplaceDepositApplicability.ALL) {
        return {
            categoryId: bookingItem.service?.businessArea ?? '',
            category: deposit.category,
            value: deposit.value
        };
    }

    if (!bookingItem.service) {
        return undefined;
    }

    return deposit.rules.find(rule => rule.categoryId === bookingItem.service?.businessArea);
};

const applyRuleToPrice = (price: number, rule: MarketplaceDepositRule): number => {
    if (price <= 0) {
        return 0;
    }

    if (rule.category === MarketplaceDepositCategory.PERCENTAGE) {
        return (price * rule.value) / 100;
    }

    return rule.value;
};

/**
 * The deposit a single booking item contributes: its service once per daycare
 * day, and each Add-On order line, which already runs over its own days.
 * Add-ons count in `all` applicability, as they always have, but
 * never in `specific`: they carry no `businessArea` and so can never match a
 * per-category rule, which is how the server calculates it.
 */
const getBookingItemDeposit = (
    bookingItem: OnlineBookingItem,
    deposit: MarketplaceDeposit
): number => {
    const rule = getDepositRuleForBookingItem(bookingItem, deposit);

    if (!rule) {
        return 0;
    }

    let itemDeposit = 0;

    if (bookingItem.service) {
        itemDeposit +=
            applyRuleToPrice(getServicePriceByPet(bookingItem.service, bookingItem.pet), rule) *
            getOnlineBookingDaycareDayCount(bookingItem);
    }

    if (
        deposit.applicability === MarketplaceDepositApplicability.ALL &&
        bookingItem.addons.length > 0
    ) {
        // Per order line, like the server: a fixed amount per line, a percentage of its total.
        itemDeposit += bookingItem.addons.reduce(
            (totalAmount, addon) =>
                totalAmount +
                getOnlineBookingAddonLineQuantities(bookingItem, addon).reduce(
                    (lineDeposit, quantity) =>
                        lineDeposit + applyRuleToPrice(addon.price * quantity, rule),
                    0
                ),
            0
        );
    }

    return itemDeposit;
};

export const getOnlineBookingTotalDeposit = createSelector(
    [getMarketplaceDeposit, getOnlineBookingItems],
    (deposit, bookingItems) => {
        // `getMarketplaceDeposit` is already undefined when the resolved
        // configuration collects nothing — see `isDepositEnabled`.
        if (!deposit) {
            return 0;
        }

        return bookingItems.reduce(
            (totalDeposit, bookingItem) =>
                totalDeposit + getBookingItemDeposit(bookingItem, deposit),
            0
        );
    }
);

/**
 * The billable total a booking item's deposit is measured against, using the
 * same add-on inclusion rule as the deposit calculation itself.
 */
const getBookingItemBillableTotal = (
    bookingItem: OnlineBookingItem,
    deposit: MarketplaceDeposit
): number => {
    if (!bookingItem.service) {
        return 0;
    }

    const servicePrice =
        getServicePriceByPet(bookingItem.service, bookingItem.pet) *
        getOnlineBookingDaycareDayCount(bookingItem);

    if (deposit.applicability !== MarketplaceDepositApplicability.ALL) {
        return servicePrice;
    }

    return (
        servicePrice +
        bookingItem.addons.reduce(
            (total, addon) => total + addon.price * getOnlineBookingAddonUnits(bookingItem, addon),
            0
        )
    );
};

/**
 * Whether any applicable line's deposit exceeds that line's own price.
 *
 * Compared per line rather than against the whole-booking total: under
 * per-category rules a single misconfigured rule can exceed its own service's
 * price while still sitting below the booking total, which a whole-booking
 * comparison would miss.
 */
export const getOnlineBookingDepositExceedsPrice = createSelector(
    [getMarketplaceDeposit, getOnlineBookingItems],
    (deposit, bookingItems) => {
        if (!deposit) {
            return false;
        }

        return bookingItems.some(bookingItem => {
            const itemDeposit = getBookingItemDeposit(bookingItem, deposit);

            if (itemDeposit <= 0) {
                return false;
            }

            return itemDeposit > getBookingItemBillableTotal(bookingItem, deposit);
        });
    }
);

/**
 * The single determination of whether the in-progress booking requires a
 * deposit, shared by the booking details preview, the agreements step and
 * booking creation so all three agree.
 *
 * `totalDeposit > 0` is what encodes "the booking includes a category with a
 * rule": an item whose category has no rule contributes nothing, so a booking
 * made up entirely of non-applicable services sums to zero. No separate
 * category cross-check is needed.
 */
export const getOnlineBookingDepositRequired = createSelector(
    [
        getOnlineBookingCustomer,
        getOnlineBookingItems,
        getOnlineBookingPreferences,
        getOnlineBookingTotalDeposit
    ],
    (customer, bookingItems, preferences, totalDeposit) =>
        customer.depositEligible &&
        !bookingRequiresApproval(bookingItems, preferences) &&
        totalDeposit > 0
);

export const getOnlineBookingItemPrice = (bookingItem: OnlineBookingItem): number => {
    if (!bookingItem.service) {
        return 0;
    }

    if (isOnlineBookingBoardingItem(bookingItem)) {
        return getServicePriceByPet(bookingItem.service, bookingItem.pet);
    }

    let price = getServicePriceByPet(bookingItem.service, bookingItem.pet);

    if (
        isOnlineBookingDaycareItem(bookingItem) &&
        bookingItem.service?.daycareMetadata?.durationOption === DAYCARE_DURATION_OPTIONS.HOURLY &&
        bookingItem.dropOffTime &&
        bookingItem.pickUpTime
    ) {
        price *= calculateDaycareHours(bookingItem.dropOffTime, bookingItem.pickUpTime);
    }

    return price;
};

const STEP_PROGRESS: Partial<Record<OnlineBookingStep, number>> = {
    [OnlineBookingStep.Home]: 5,
    [OnlineBookingStep.Signup]: 10,
    [OnlineBookingStep.Signin]: 10,
    [OnlineBookingStep.CreatePet]: 16,
    [OnlineBookingStep.PetList]: 22,
    [OnlineBookingStep.Agreements]: 80,
    [OnlineBookingStep.Checkout]: 90,
    [OnlineBookingStep.Success]: 100,
    [OnlineBookingStep.JoinWaitlist]: 80,
    [OnlineBookingStep.WaitlistSuccess]: 100,
    [OnlineBookingStep.Purchase]: 50,
    [OnlineBookingStep.PurchaseCheckout]: 90,
    [OnlineBookingStep.PurchaseSuccess]: 100
};

const SERVICES_BAND_START = 25;
const SERVICES_BAND_END = 75;
const SERVICES_BAND_DECAY = 0.5;

// Navigation order, read off each step's back/submit handlers. OnlineBookingStep and
// the wizard component maps are lookup tables and carry no ordering.
const CATEGORY_FLOW: Partial<Record<MarketplaceServiceCategory, Array<OnlineBookingStep>>> = {
    [MarketplaceServiceCategory.GROOMING]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.StaffList,
        OnlineBookingStep.DatePicker
    ],
    [MarketplaceServiceCategory.DENTAL]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.StaffList,
        OnlineBookingStep.DatePicker
    ],
    [MarketplaceServiceCategory.DAYCARE]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.DatePicker,
        OnlineBookingStep.TimePicker,
        OnlineBookingStep.AddonList,
        OnlineBookingStep.Instructions
    ],
    [MarketplaceServiceCategory.BOARDING]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.DatePicker,
        OnlineBookingStep.TimePicker,
        OnlineBookingStep.AddonList,
        OnlineBookingStep.Instructions
    ],
    [MarketplaceServiceCategory.EVALUATION]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.DatePicker
    ],
    [MarketplaceServiceCategory.WALKING]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.StaffList,
        OnlineBookingStep.DatePicker
    ],
    [MarketplaceServiceCategory.TRAINING]: [
        OnlineBookingStep.ServiceList,
        OnlineBookingStep.StaffList,
        OnlineBookingStep.DatePicker
    ]
};

const getHubProgress = (completedCategories: number) =>
    SERVICES_BAND_START +
    (SERVICES_BAND_END - SERVICES_BAND_START) *
        (1 - Math.pow(SERVICES_BAND_DECAY, completedCategories));

export const getOnlineBookingProgress = createSelector(
    [getOnlineBookingCurrentStep, getOnlineBookingState],
    (currentStep, { currentCategory, completedCategories }) => {
        const stepProgress = STEP_PROGRESS[currentStep];

        if (stepProgress !== undefined) {
            return stepProgress;
        }

        if (currentStep === OnlineBookingStep.ServiceCategory) {
            return getHubProgress(completedCategories);
        }

        const flow = currentCategory ? CATEGORY_FLOW[currentCategory] : undefined;
        const stepIndex = flow?.indexOf(currentStep) ?? -1;

        if (!flow || stepIndex < 0) {
            return getHubProgress(completedCategories);
        }

        const from = getHubProgress(completedCategories);
        const to = getHubProgress(completedCategories + 1);
        const positionsIncludingHubReturn = flow.length + 1;

        return from + (to - from) * ((stepIndex + 1) / positionsIncludingHubReturn);
    }
);

export const getOnlineBookingTotalAmount = createSelector(
    [getMarketplaceDeposit, getOnlineBookingItems],
    (deposit, bookingItems) => {
        return bookingItems.reduce((totalAmount, bookingItem) => {
            let serviceAmount = getOnlineBookingItemPrice(bookingItem);

            if (isOnlineBookingBoardingItem(bookingItem)) {
                const nights = bookingItem.endDate?.diff(bookingItem.startDate, 'days');

                if (nights) {
                    serviceAmount *= nights;
                }
            }

            // An Add-On is priced by its days and quantities, which already include the daycare days.
            const addonsAmount = bookingItem.addons.reduce(
                (total, addon) =>
                    total + addon.price * getOnlineBookingAddonUnits(bookingItem, addon),
                0
            );

            return (
                totalAmount +
                serviceAmount * getOnlineBookingDaycareDayCount(bookingItem) +
                addonsAmount
            );
        }, 0);
    }
);
